import { forwardRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { KeenIcon } from '@/components';
import { DatePicker, TimePicker, LazyFormikSelect } from '@/components/form';
import { useUserOptions } from '@/hooks';

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(255, 'Title is too long'),
  due_date: Yup.string().required('Due date is required'),
  due_time: Yup.string().required('Due time is required'),
  notes: Yup.string().nullable(),
  assigned_to: Yup.array().nullable(),
});

/**
 * Create/edit a reminder. When opened from a contact or matter tab the parent is
 * passed in via parentType/parentId and submitted silently; opened from the
 * dashboard with no parent, it creates a general reminder.
 */
const ReminderModal = forwardRef(({
  open,
  onClose,
  mode = 'create',
  data,
  onSubmit,
  parentType = null,
  parentId = null,
  createLoading,
  updateLoading,
}, formikRef) => {
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });

  if (!open) return null;

  // due_date/due_time are raw server-side splits of due_at — the display-
  // formatted due_at string is not safe to parse here.
  const assigned = data?.assigned_to || [];

  const initialValues = {
    title: data?.title || '',
    notes: data?.notes || '',
    due_date: data?.due_date || '',
    due_time: data?.due_time || '',
    assigned_to: assigned.map((user) => user.id),
    assigned_to_user: assigned.map((user) => ({
      label: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
      value: user.id,
    })),
  };

  const handleSubmit = (values, formikHelpers) => {
    // assigned_to_user only seeds the select's preloaded labels — it never ships.
    onSubmit({
      title: values.title,
      notes: values.notes,
      assigned_to: values.assigned_to,
      due_at: `${values.due_date} ${values.due_time}:00`,
      ...(parentType && parentId
        ? { remindable_type: parentType, remindable_id: parentId }
        : {}),
    }, formikHelpers);
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit: submitForm }) => (
          <Form onSubmit={submitForm} className="modal-animate-scale modal-body w-4/5 max-w-lg relative">
            <h3 className="card-title capitalize mb-5">{mode} Task</h3>
            <button type="button" onClick={onClose} className="modal-close-btn">
              <KeenIcon icon="cross" />
            </button>

            <div>
              <label className="form-label mb-1">Title <span className="pl-px text-red-600">*</span></label>
              <Field name="title" className="input" placeholder="e.g. Call the client" />
              <ErrorMessage name="title" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-4 my-4">
              <div>
                <label className="form-label mb-1">Due Date <span className="pl-px text-red-600">*</span></label>
                <Field name="due_date" component={DatePicker} disablePast />
                <ErrorMessage name="due_date" component="div" className="text-red-500 text-xs mt-1" />
              </div>
              <div>
                <label className="form-label mb-1">Due Time <span className="pl-px text-red-600">*</span></label>
                <Field name="due_time" component={TimePicker} use12Hour />
                <ErrorMessage name="due_time" component="div" className="text-red-500 text-xs mt-1" />
              </div>
            </div>

            <div className="my-4">
              <label className="form-label mb-1">Assigned To</label>
              <LazyFormikSelect
                name="assigned_to"
                placeholder="Select Assignee"
                loadOptions={loadUserOptions}
                pageSize={20}
                isMulti={true}
                preloadedOption={initialValues.assigned_to_user}
              />
              <ErrorMessage name="assigned_to" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className="my-4">
              <label className="form-label mb-1">Notes</label>
              <Field as="textarea" name="notes" rows={3} className="input h-auto py-2" />
              <ErrorMessage name="notes" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <button
              type="submit"
              disabled={createLoading || updateLoading}
              className="btn btn-primary justify-center w-full mt-2"
            >
              {mode === 'edit'
                ? (updateLoading ? 'Please Wait...' : 'Update Task')
                : (createLoading ? 'Please Wait...' : 'Create Task')}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
});

export { ReminderModal };
